/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useAllOrgSettings, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { useCallback, useMemo } from 'react';
import { ORG_SETTINGS_SPACING } from './OrgSettingsStyleLib';
import { useSettingsAutosave } from './useSettingsAutosave';

import {
  getOrgSettingsProjectTypesOrDefaults,
  ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS,
  type ProjectTypesRecord,
} from '@/Client/State';
import { useToast } from '@/Client/State/ToastContext';
import { SettingsHeaderRow, SettingsInsightBanner } from '@/Client/Widgets';

import { ModifiersSection } from './ModifiersSection';
import { MODIFIER_INSIGHT, PROJECT_SETTINGS_INSIGHT } from './orgSettingsInsights';
import { SettingsSaveQueueProvider } from './settingsSaveQueue';
import { WorkflowProjectSettingsItem } from './WorkflowProjectSettingsItem';

type ProjectTypeSettings = ProjectTypesRecord[string];

/* The mockup's `.stack`: cards in a column with the page's own inset, rather than full-width
   rows separated by dividers (KAIDEV-4345). */
const CARD_STACK_STYLE = css`
  label: CARD_STACK_STYLE;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 0 ${ORG_SETTINGS_SPACING.gutterX}px;
`;

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding-bottom: 60px;
`;

const normalizeProjectTypes = (
  projectTypes: ProjectTypesRecord,
): ProjectTypesRecord => getOrgSettingsProjectTypesOrDefaults(projectTypes);

/**
 * Org Settings → Workflow: Project Settings, then Modifiers beneath it (KAIDEV-4345).
 *
 * Modifiers used to hang off this tab's dropdown as a page of its own. Two short sections on one
 * page beat a menu hiding one of them, and both autosave, so neither needs a save control and
 * the page needs no navigation guard.
 */
const WorkflowSections = () => {
  const { allOrgSettings, allOrgSettingsMutators } = useAllOrgSettings();
  const { featureFlags } = useFeatureFlags();
  const modifierSettingsEnabled = !!featureFlags.modifierSettings;
  const { showErrorToast } = useToast();

  const persistedProjectTypes = useMemo(
    () =>
      getOrgSettingsProjectTypesOrDefaults(
        allOrgSettings?.projectSettings?.types as ProjectTypesRecord | undefined,
      ),
    [allOrgSettings?.projectSettings?.types],
  );

  /* Every change persists as it is made, as the scope editor does: no save control, no cancel,
     and no navigation guard to protect an edit that is already stored (KAIDEV-4345). */
  /* Rethrown after reporting: the hook reverts the field that failed, and without a word on the
     page that revert would read as the edit simply undoing itself. */
  const save = useCallback(async (record: ProjectTypesRecord) => {
    try {
      await allOrgSettingsMutators.update({ projectSettings: { types: normalizeProjectTypes(record) } });
    } catch (error) {
      showErrorToast(error, 'save project settings');
      throw error;
    }
  }, [allOrgSettingsMutators, showErrorToast]);

  const { draft: draftProjectTypes, updateEntry, commitEntry, focusEntry } = useSettingsAutosave<ProjectTypeSettings>(
    persistedProjectTypes,
    /* `persistedProjectTypes` is built with the same normalizer the save sends through, so the
       stored form is what the hook must compare against to know the settings have caught up. */
    { save, toStoredForm: normalizeProjectTypes },
  );

  const normalizedDraftProjectTypes = useMemo(
    () => normalizeProjectTypes(draftProjectTypes),
    [draftProjectTypes],
  );

  const enabledProjectTypeCount = ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.filter(
    ({ key }) => normalizedDraftProjectTypes[key]?.isEnabled !== false,
  ).length;
  const disabledProjectTypeCount = ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.length - enabledProjectTypeCount;
  const counts = {
    lead: `${ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.length} project types`,
    details: [`${enabledProjectTypeCount} enabled`, `${disabledProjectTypeCount} disabled`],
  };

  // Text waits out the typing; a toggle is a decision already made, so it saves at once.
  const handleProjectTypeUpdate = (
    projectTypeKey: string,
    updates: Partial<ProjectTypeSettings>,
  ) => updateEntry(projectTypeKey, { ...draftProjectTypes[projectTypeKey], ...updates });

  const handleToggleProjectType = (projectTypeKey: string) =>
    updateEntry(
      projectTypeKey,
      {
        ...draftProjectTypes[projectTypeKey],
        isEnabled: !(draftProjectTypes[projectTypeKey]?.isEnabled ?? true),
      },
      { immediate: true },
    );

  return (
    <div css={CONTENT_STYLE}>
      {
        /* One banner for the page, rotating through what both its sections have to say. Pinning
           a second one to Modifiers put a banner halfway down the page (KAIDEV-4345). */
      }
      <SettingsInsightBanner
        insights={modifierSettingsEnabled
          ? [PROJECT_SETTINGS_INSIGHT, MODIFIER_INSIGHT]
          : [PROJECT_SETTINGS_INSIGHT]}
      />
      {
        /* No header button: the page autosaves, and Project Settings has nothing to add. */
      }
      <SettingsHeaderRow counts={counts} hideDefaultActions />
      <div css={CARD_STACK_STYLE}>
        {ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.map(projectTypeDefinition => {
          const projectType = draftProjectTypes[projectTypeDefinition.key] ?? {};

          return (
            <WorkflowProjectSettingsItem
              key={projectTypeDefinition.key}
              baseProjectTypeLabel={projectTypeDefinition.displayName}
              baseProjectTypeDescription={projectTypeDefinition.description}
              projectTypeLabel={projectType.displayName ?? ''}
              projectDescription={projectType.description ?? ''}
              enabled={projectType.isEnabled ?? true}
              onToggleClick={() => handleToggleProjectType(projectTypeDefinition.key)}
              onProjectTypeLabelChange={displayName => handleProjectTypeUpdate(projectTypeDefinition.key, { displayName })}
              onProjectDescriptionChange={description => handleProjectTypeUpdate(projectTypeDefinition.key, { description })}
              onFieldBlur={() => commitEntry(projectTypeDefinition.key)}
              onFieldFocus={() => focusEntry(projectTypeDefinition.key)}
            />
          );
        })}
      </div>

      {modifierSettingsEnabled ? <ModifiersSection /> : null}
    </div>
  );
};

/**
 * The page's two sections share one save queue.
 *
 * Both write `allOrgSettings`, and `updateAllOrgSettings` merges onto what it read at the start
 * of the request, so overlapping writes lose each other's edits — blurring a project description
 * by clicking a modifier toggle is enough (KAIDEV-4345 review). The provider has to sit above the
 * component that calls the autosave hook, which is why the sections are a child rather than this
 * component's own body. A page that grows a second writing section needs the same wrapper.
 */
export const WorkflowTabContent = () => (
  <SettingsSaveQueueProvider>
    <WorkflowSections />
  </SettingsSaveQueueProvider>
);
